<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="category-modal">
      <div class="modal-header">
        <h2 class="text-lg font-medium text-slate-900">
          {{ $t('BUSINESS_DATA.CATEGORY.ADD_NEW') }}
        </h2>
      </div>
      
      <form @submit.prevent="onSubmit" class="modal-form">
        <!-- Category Type -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.CATEGORY.TYPE') }} *
          </label>
          <select
            v-model="formData.category_type"
            class="form-select"
            required
          >
            <option value="">{{ $t('BUSINESS_DATA.CATEGORY.SELECT_TYPE') }}</option>
            <option value="faq">{{ $t('BUSINESS_DATA.CATEGORY.FAQ') }}</option>
            <option value="data">{{ $t('BUSINESS_DATA.CATEGORY.DATA') }}</option>
            <option value="image">{{ $t('BUSINESS_DATA.CATEGORY.IMAGE') }}</option>
          </select>
        </div>
        
        <!-- Name field -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.CATEGORY.NAME') }} *
          </label>
          <input
            v-model="formData.name"
            :placeholder="$t('BUSINESS_DATA.CATEGORY.NAME_PLACEHOLDER')"
            class="form-input"
            required
            maxlength="100"
          />
          <div class="form-help text-right">
            <span :class="formData.name.length > 80 ? 'text-red-500' : 'text-gray-500'">
              {{ formData.name.length }}/100
            </span>
          </div>
        </div>
        
        <!-- Color field -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.CATEGORY.COLOR') }} *
          </label>
          <div class="flex gap-2 items-center">
            <input
              v-model="formData.color"
              type="color"
              class="form-color-input"
            />
            <input
              v-model="formData.color"
              :placeholder="$t('BUSINESS_DATA.CATEGORY.COLOR_PLACEHOLDER')"
              class="form-input flex-1"
              pattern="^#[0-9A-Fa-f]{6}$"
              required
            />
          </div>
          <div class="form-help">
            {{ $t('BUSINESS_DATA.CATEGORY.COLOR_HELP') }}
          </div>
        </div>
        
        <!-- Preview -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.CATEGORY.PREVIEW') }}
          </label>
          <div class="category-preview">
            <span
              class="category-badge"
              :style="{ backgroundColor: formData.color, color: getTextColor(formData.color) }"
            >
              {{ formData.name || $t('BUSINESS_DATA.CATEGORY.PREVIEW_PLACEHOLDER') }}
            </span>
          </div>
        </div>

        <!-- Existing categories list (manage) -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.CATEGORY.SELECT_CATEGORY') }}
          </label>
          <div class="space-y-2 max-h-56 overflow-auto border border-gray-200 rounded-md p-2">
            <div
              v-for="cat in filteredCategories"
              :key="cat.id"
              class="flex items-center justify-between gap-2"
            >
              <div class="flex items-center gap-2 min-w-0">
                <span
                  class="category-badge"
                  :style="{ backgroundColor: cat.color, color: getTextColor(cat.color) }"
                >
                  {{ cat.name }}
                </span>
                <span class="text-xs text-gray-500 truncate">#{{ cat.id }}</span>
              </div>
              <div class="flex items-center gap-1">
                <woot-button variant="clear" size="small" @click.prevent.stop="startEdit(cat)">
                  <fluent-icon icon="edit" size="14" />
                </woot-button>
                <woot-button variant="clear" size="small" color-scheme="danger" @click.prevent.stop="deleteCategory(cat)">
                  <fluent-icon icon="delete" size="14" />
                </woot-button>
              </div>
            </div>
            <div v-if="!filteredCategories.length" class="text-sm text-gray-500">
              {{ $t('BUSINESS_DATA.EMPTY.TITLE') }}
            </div>
          </div>
        </div>
        
        <!-- Actions -->
        <div class="modal-footer">
          <woot-button
            type="button"
            variant="clear"
            @click.prevent.stop="onClose"
          >
            {{ $t('COMMON.CANCEL') }}
          </woot-button>
          
          <woot-button
            type="submit"
            color-scheme="primary"
            :loading="isSaving"
            :disabled="!isFormValid"
          >
            {{ $t('COMMON.CREATE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
import { useAlert } from 'dashboard/composables';

export default {
  name: 'CategoryModal',
  
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    categoryType: {
      type: String,
      default: '',
    },
  },
  
  data() {
    return {
      isSaving: false,
      formData: {
        name: '',
        color: '#6B7280',
        category_type: '',
      },
      isLoadingList: false,
      categories: [],
      editingId: null,
      editForm: { name: '', color: '#6B7280' },
    };
  },
  
  computed: {
    isFormValid() {
      return this.formData.name.trim() && 
             this.formData.color &&
             this.formData.category_type &&
             this.formData.name.length <= 100;
    },
    
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },

    filteredCategories() {
      const type = this.formData.category_type || this.categoryType;
      return (this.categories || []).filter(c => c.type === type);
    },
  },
  
  watch: {
    categoryType: {
      immediate: true,
      handler(newType) {
        if (newType) {
          this.formData.category_type = newType;
        }
      },
    },
    
    show(newShow) {
      if (!newShow) {
        this.resetForm();
      } else {
        this.loadCategories();
      }
    },
  },
  
  methods: {
    resetForm() {
      this.formData = {
        name: '',
        color: '#6B7280',
        category_type: this.categoryType || '',
      };
      this.editingId = null;
      this.editForm = { name: '', color: '#6B7280' };
    },
    
    getTextColor(backgroundColor) {
      if (!backgroundColor) return '#000000';
      
      // Convert hex to RGB
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Calculate luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    },
    
    async onSubmit() {
      if (!this.isFormValid) {
        return;
      }
      
      this.isSaving = true;
      try {
        const payload = {
          name: this.formData.name.trim(),
          color: this.formData.color,
          category_type: this.formData.category_type,
          is_active: true,
        };
        
        const response = await window.axios.post(
          `/api/v1/accounts/${this.currentAccountId}/business_categories`,
          payload
        );
        
        this.$emit('created', response.data);
        useAlert(this.$t('BUSINESS_DATA.CATEGORY.CREATE_SUCCESS'), 'success');
        await this.loadCategories();
        this.onClose();
      } catch (error) {
        console.error('Error creating category:', error);
        
        let errorMessage = this.$t('BUSINESS_DATA.CATEGORY.CREATE_FAILED');
        
        if (error?.response?.data?.errors) {
          errorMessage = error.response.data.errors.join(', ');
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        useAlert(errorMessage, 'error');
      } finally {
        this.isSaving = false;
      }
    },
    
    async loadCategories() {
      if (!this.currentAccountId) return;
      this.isLoadingList = true;
      try {
        const { data } = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/business_categories`, { params: { per_page: 200 } });
        this.categories = data.business_categories || [];
      } catch (e) {
        // ignore
      } finally {
        this.isLoadingList = false;
      }
    },

    startEdit(cat) {
      this.editingId = cat.id;
      this.editForm = { name: cat.name, color: cat.color };
      // put inline editors into the list (simple prompt fallback could be used instead)
      this.$nextTick(async () => {
        try {
          const payload = { name: this.editForm.name, color: this.editForm.color };
          await window.axios.put(`/api/v1/accounts/${this.currentAccountId}/business_categories/${cat.id}`, payload);
          await this.loadCategories();
          useAlert(this.$t('COMMON.UPDATE'), 'success');
        } catch (e) {
          useAlert(this.$t('BUSINESS_DATA.ERROR.UPDATE_FAILED'), 'error');
        } finally {
          this.editingId = null;
        }
      });
    },

    async deleteCategory(cat) {
      if (!window.confirm(this.$t('BUSINESS_DATA.ITEMS.DELETE_CONFIRM') || 'Xoá danh mục?')) return;
      try {
        await window.axios.delete(`/api/v1/accounts/${this.currentAccountId}/business_categories/${cat.id}`);
        await this.loadCategories();
        useAlert(this.$t('BUSINESS_DATA.ITEMS.DELETED_SUCCESS') || 'Đã xoá', 'success');
      } catch (e) {
        useAlert(this.$t('BUSINESS_DATA.ERROR.DELETE_FAILED'), 'error');
      }
    },

    onClose() {
      this.$emit('close');
    },
  },
  
  emits: ['close', 'created'],
};
</script>

<style lang="scss" scoped>
.category-modal {
  width: 100%;
  max-width: 500px;
  padding: 24px;
}

.modal-header {
  margin-bottom: var(--space-large);
  padding-top: 8px;
  
  h2 {
    margin: 0;
    color: var(--color-heading);
    font-size: var(--font-size-large);
  }
}

.modal-form {
  .form-row {
    margin-bottom: var(--space-normal);
    
    .form-label {
      display: block;
      margin-bottom: var(--space-smaller);
      font-weight: var(--font-weight-medium);
      color: var(--color-heading);
    }
    
    .form-input,
    .form-select {
      width: 100%;
      padding: var(--space-small);
      border: 1px solid var(--color-border);
      border-radius: var(--border-radius-normal);
      font-size: var(--font-size-default);
      
      &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 1px var(--color-primary);
      }
    }
    
    .form-color-input {
      width: 50px;
      height: 40px;
      padding: 0;
      border: 1px solid var(--color-border);
      border-radius: var(--border-radius-normal);
      cursor: pointer;
      
      &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 1px var(--color-primary);
      }
    }
    
    .form-help {
      margin-top: var(--space-smaller);
      font-size: var(--font-size-small);
      color: var(--color-text-light);
    }
  }
}

.category-preview {
  padding: var(--space-small);
  background: var(--color-background-light);
  border-radius: var(--border-radius-normal);
  border: 1px solid var(--color-border-light);
}

.category-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  text-align: center;
  min-width: 80px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  margin-top: var(--space-large);
  padding-top: var(--space-normal);
  border-top: 1px solid var(--color-border-light);
}

@media (max-width: 768px) {
  .category-modal {
    max-width: 100%;
  }
}
</style>
